if (!customElements.get('size-chart')) {
  /** @type {'cm' | 'in' | null} */
  let preferredSizeChartUnit = null;

  /** @type {AbortController | null} */
  let unitController = null;

  /**
   * @param {'cm' | 'in'} unit - Measurement unit to apply
   * @param {{ syncRadios?: boolean, source?: HTMLInputElement | null, updateDetails?: boolean, productUrl?: string | null }} [options] - Apply options
   */
  async function applySizeFitUnit(unit, options = {}) {
    const { syncRadios = false, source = null, updateDetails = true, productUrl = null } = options;

    if (syncRadios) {
      for (const input of document.querySelectorAll(`input[name$="-size-chart-unit"][value="${unit}"]`)) {
        if (input instanceof HTMLInputElement && input !== source) {
          input.checked = true;
        }
      }
    }

    if (unitController) {
      unitController.abort();
    }

    unitController = new AbortController();

    const sectionId = unit === 'in' ? 'section-ajax-product-size-fit-in' : 'section-ajax-product-size-fit-cm';
    const path =
      productUrl ||
      document.querySelector('[data-product-url]')?.getAttribute('data-product-url') ||
      window.location.pathname;
    const url = `${path}?sections=${sectionId}`;

    try {
      const response = await fetch(url, { signal: unitController.signal });

      if (!response.ok) return;

      const data = await response.json();
      const sectionHtml = data[sectionId];

      if (!sectionHtml) return;

      const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const nextDetails = html.querySelector('.product-details-size-and-fit-size-details');
      const nextModelAndFit = html.querySelector('.product-form-model-and-fit');

      if (updateDetails && nextDetails) {
        for (const current of document.querySelectorAll(
          '.product-details-size-and-fit > .product-details-size-and-fit-size-details',
        )) {
          current.replaceWith(nextDetails.cloneNode(true));
        }
      }

      if (nextModelAndFit) {
        for (const current of document.querySelectorAll('.product-form-model-and-fit')) {
          current.replaceWith(nextModelAndFit.cloneNode(true));
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      throw error;
    }
  }

  document.addEventListener('product:variant-changed-section-data', (event) => {
    if (!preferredSizeChartUnit) return;

    const unit = preferredSizeChartUnit;
    const section = event.detail?.section;
    const productUrl = section?.querySelector('[data-product-url]')?.getAttribute('data-product-url') || null;

    // product-form is replaced synchronously after this event with market-default
    // use_imperial markup; re-apply the buyer's unit preference once that swap lands.
    // Always refresh Size & Fit details too: an in-flight unit toggle is aborted here,
    // and skipping details would leave the accordion/modal on the previous unit.
    queueMicrotask(() => {
      applySizeFitUnit(unit, { productUrl });
    });
  });

  customElements.define(
    'size-chart',
    class SizeChart extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLSelectElement | null} */
        this.select = this.querySelector('[data-size-chart-head-select]');

        /** @type {HTMLElement[]} */
        this.values = Array.from(this.querySelectorAll('[data-size-chart-head-value]'));
      }

      connectedCallback() {
        this.select?.addEventListener('change', this.#handleChange);

        for (const input of this.querySelectorAll('input[name$="-size-chart-unit"]')) {
          input.addEventListener('change', this.#handleUnitChange);
        }
      }

      #handleChange = () => {
        for (const value of this.values) {
          value.dataset.selected = String(value.dataset.sizeChartHeadValue === this.select?.value);
        }
      };

      /** @param {Event} event - The unit toggle change event */
      #handleUnitChange = async (event) => {
        const target = event.target;

        if (!(target instanceof HTMLInputElement) || !target.checked) return;

        const unit = target.value;

        if (unit !== 'cm' && unit !== 'in') return;

        preferredSizeChartUnit = unit;

        await applySizeFitUnit(unit, { syncRadios: true, source: target });
      };
    },
  );
}
